import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  IconButton,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import toast, { Toaster } from "react-hot-toast";
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import HotelTableRow from "../hotel-table-row";
import HotelTableToolbar from "../hotel-table-toolbar";
import {
  createHotel,
  hotelData,
  hotelBrandMyData,
} from "../../../features/dataAction";
import {
  applyFilter,
  emptyRows,
  getComparator,
} from "../../../layouts/dashboard/common/utils";
import EmptyRows from "../../../layouts/dashboard/common/Empty-rows/EmptyRows";
import NoData from "../../../layouts/dashboard/common/No-data/No-data";
import TableHeadCustom from "../../../layouts/dashboard/common/Table-head/Table-Head";
import CloseIcon from "@mui/icons-material/Close";

// ----------------------------------------------------------------------

const initialFormState = {
  name: "",
  address: "",
  phone: "",
  adminName: "",
  adminEmail: "",
  adminPassword: "",
};

const validateForm = (fields) => {
  const errors = {};
  if (!fields.name) errors.name = "Name is required";
  if (!fields.address) errors.address = "Address is required";
  if (!fields.phone) {
    errors.phone = "Phone is required";
  } else if (!/^[0-9]{10}$/.test(fields.phone.trim())) {
    errors.phone = "Phone number must be exactly 10 digits";
  }
  if (!fields.adminName) errors.adminName = "Admin Name is required";
  if (!fields.adminEmail) errors.adminEmail = "Admin Email is required";
  if (!fields.adminPassword)
    errors.adminPassword = "Admin Password is required";
  return errors;
};

export default function HotelViewPage() {
  const dispatch = useDispatch();
  const { hotelDatas, loading, error, createHotelLoading, createHotelSuccess } = useSelector((state) => {
    return {
      hotelDatas: state.hotelReducer.hotelDatas?.hotelBrands,
      loading: state.hotelReducer.loading,
      error: state.hotelReducer.error,
      createHotelLoading: state.hotelReducer.createHotelLoading,
      createHotelSuccess: state.hotelReducer.createHotelSuccess,
    };
  });
  

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [selected, setSelected] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRole = localStorage.getItem("role");
  useEffect(() => {
    if (!userRole) return;
    if (userRole === "admin") {
      dispatch(hotelBrandMyData());
    } else {
      dispatch(hotelData());
    }
  }, [dispatch, userRole]);
  useEffect(() => {
    if (hotelDatas && hotelDatas.length > 0) {
      setFirstLoadDone(true);
    }
  }, [hotelDatas]);

  useEffect(() => {
    if (createHotelSuccess && !openCreate) {
      toast.success("Hotel created successfully!");
      if (userRole === "admin") {
        dispatch(hotelBrandMyData());
      } else {
        dispatch(hotelData());
      }
    }
  }, [createHotelSuccess, openCreate, userRole, dispatch]);

  useEffect(() => {
    if (error && isSubmitting) {
      toast.error(error || "Failed to create hotel");
      setIsSubmitting(false);
    }
  }, [error, isSubmitting]);

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(id);
  };

  const handleSelectAllClick = (event) => {
    setSelected(event.target.checked ? hotelDatas.map((n) => n.name) : []);
  };

  const handleFormChange = (field) => (e) => {
    let { value } = e.target;

    if (field === "phone") {
      value = value.replace(/\D/g, "");
      if (value.length > 10) value = value.slice(0, 10);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = () => {
    console.log("Submit button clicked"); // Debug log
    
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    console.log("Dispatching createHotel action with data:", formData); // Debug log

    // Simple dispatch without promise handling
   dispatch(createHotel(
    formData.name,           // name
    formData.address,        // address
    formData.phone,          // phone
    formData.adminName,      // adminName
    formData.adminEmail,     // adminEmail
    formData.adminPassword,  // adminPassword
    "admin",                 // userRole (or get from your state)
    (errorMsg) => {          // onError callback
      toast.error(errorMsg);
      setIsSubmitting(false);
    }
  ));

    setOpenCreate(false);
    setFormData(initialFormState);
    
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".xlsx")) {
      setFileName(file.name);
    } else {
      alert("Please select a valid .xlsx file.");
    }
  };

  const dataFiltered = applyFilter({
    inputData: hotelDatas || [],
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const notFound = !dataFiltered.length;

  return (
    <Container>
      <Toaster position="top-right" />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Box>
          <Typography variant="h4" color="primary">
            Hotel List
          </Typography>
          <Typography sx={{ fontSize: 13, color: "gray" }}>
            Hotel List
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setOpenCreate(true)}
        >
          Create Hotel
        </Button>
      </Stack>

      <CreateHotelModal
        open={openCreate}
        onClose={() => {
          setOpenCreate(false);
          setFormData(initialFormState);
          setErrors({});
        }}
        formData={formData}
        errors={errors}
        onChange={handleFormChange}
        onSave={handleSubmit}
        isSubmitting={isSubmitting || createHotelLoading}
        onReset={() => setFormData(initialFormState)}
      />

      <UploadFileModal
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        filename={fileName}
        onFileChange={handleFileUpload}
      />

      <Card>
        <HotelTableToolbar
          numSelected={selected.length}
          filterName={filterName}
          onFilterName={(e) => {
            setPage(0);
            setFilterName(e.target.value);
          }}
        />
        <Scrollbar>
          <TableContainer>
            <Table>
              <TableHeadCustom
                order={order}
                orderBy={orderBy}
                rowCount={hotelDatas?.length || 0}
                numSelected={selected.length}
                onRequestSort={handleSort}
                onSelectAllClick={handleSelectAllClick}
                headLabel={[
                  { id: "Id", label: "Id", width: "20%" },
                  { id: "Name", label: "Name", width: "15%" },
                  { id: "Address", label: "Address", width: "30%" },
                  { id: "Phone", label: "Phone", width: "15%" },
                  {
                    id: "Action",
                    label: "Action",
                    width: "10%",
                    align: "right",
                  },
                ]}
              />
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ padding: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "200px",
                          width: "100%",
                        }}
                      >
                        <CircularProgress size={30} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : dataFiltered.length > 0 ? (
                  <>
                    {dataFiltered
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row) => (
                        <HotelTableRow
                          key={row._id}
                          id={row._id}
                          name={row.name}
                          address={row.address}
                          phone={row.phone}
                          adminUser={row.adminUser?.name}
                          adminEmail={row.adminUser?.email}
                        />
                      ))}
                    <EmptyRows
                      height={77}
                      emptyRows={emptyRows(
                        page,
                        rowsPerPage,
                        hotelDatas?.length || 0
                      )}
                    />
                  </>
                ) : (
                 <NoData query={filterName} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          component="div"
          count={hotelDatas?.length || 0}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setPage(0);
            setRowsPerPage(parseInt(e.target.value, 10));
          }}
        />
      </Card>
    </Container>
  );
}

function CreateHotelModal({
  open,
  onClose,
  formData,
  errors,
  onChange,
  onSave,
  onReset,
  isSubmitting = false,
}) {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "20px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#00A651" }}>
            Add New Hotel
          </Typography>

          <IconButton onClick={() => {
            onReset();
            onClose();
          }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {[
          { key: "name", label: "Name" },
          { key: "address", label: "Address" },
          { key: "phone", label: "Phone" },
          { key: "adminName", label: "Admin Name" },
          { key: "adminEmail", label: "Admin Email" },
          { key: "adminPassword", label: "Admin Password", type: "password" },
        ].map((field) => (
          <TextField
            key={field.key}
            label={field.label + "*"}
            value={formData[field.key]}
            onChange={onChange(field.key)}
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            error={!!errors[field.key]}
            helperText={errors[field.key]}
            type={field.type || (field.key === "phone" ? "number" : "text")}
            inputProps={
              field.key === "phone"
                ? { maxLength: 10, inputMode: "numeric" } 
                : {}
            }
            disabled={isSubmitting}
          />
        ))}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="success"
            onClick={() => {
              onReset();
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={onSave}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? "Creating..." : "Save"}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

function UploadFileModal({ open, onClose, filename, onFileChange }) {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={uploadModalStyle}>
        <Typography variant="h6" mb={2}>
          Upload File
        </Typography>
        <Box display="flex" alignItems="center" mb={2}>
          <label
            htmlFor="fileInput"
            style={{ cursor: "pointer", marginRight: "10px" }}
          >
            {filename || "Upload .xlsx File"}
          </label>
          <input
            id="fileInput"
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
          <Button variant="contained" color="primary" size="small">
            Upload
          </Button>
        </Box>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" color="success" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="success">
            Save
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 650,
  width: "90%",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  p: { xs: "20px", sm: "34px" },
};

const uploadModalStyle = {
  ...modalStyle,
  width: 500,
};